import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Layers, Building2, Grid3X3, Settings, Phone, Mail } from 'lucide-react';
import { companyInfo, services, whyChooseUs, portfolioItems, serviceDetails } from '../data/mock';

const iconMap = {
  Layers: Layers,
  Building2: Building2,
  Grid3X3: Grid3X3,
  Settings: Settings
};

const HomePage = () => {
  const featuredPortfolio = portfolioItems.slice(0, 6);

  return (
    <main>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-white overflow-hidden">
        {/* Yellow Accent Line */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 sm:w-2 bg-yellow-500" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 pt-28 sm:py-32 sm:pt-40 w-full">
          <div className="max-w-3xl">
            {/* Logo */}
            <div className="mb-6 sm:mb-8 animate-fade-in">
              <img 
                src={companyInfo.logo} 
                alt="Barchalo Logo" 
                className="h-28 sm:h-40 md:h-56 lg:h-72 w-auto"
              />
            </div>

            <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6 animate-fade-in-up animation-delay-200">
              <div className="h-1 w-12 sm:w-16 bg-yellow-500" />
              <span className="text-yellow-600 font-bold tracking-wider text-xs sm:text-sm uppercase">
                {companyInfo.tagline}
              </span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 leading-tight mb-4 sm:mb-6 animate-fade-in-up animation-delay-400 uppercase tracking-tight">
              {companyInfo.heroHeadline}
            </h1>
            
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-8 sm:mb-10 max-w-2xl animate-fade-in-up animation-delay-600">
              {companyInfo.heroSubtext}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 animate-fade-in-up animation-delay-800">
              <Link
                to="/portfolio"
                data-testid="hero-portfolio-btn"
                className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 bg-yellow-500 text-slate-900 font-bold uppercase tracking-wide rounded-sm hover:bg-yellow-400 transition-all duration-300 shadow-md hover:shadow-lg text-sm sm:text-base min-h-[48px]"
              >
                View Portfolio
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-quote-btn"
                className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 border-2 border-slate-300 text-slate-900 font-bold uppercase tracking-wide rounded-sm hover:border-yellow-500 hover:text-yellow-600 transition-all duration-300 text-sm sm:text-base min-h-[48px]"
              >
                Get a Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <div className="flex items-center justify-center gap-3 sm:gap-4 mb-4">
              <div className="h-1 w-8 sm:w-12 bg-yellow-500" />
              <span className="text-yellow-600 font-bold tracking-wider text-xs sm:text-sm uppercase">What We Do</span>
              <div className="h-1 w-8 sm:w-12 bg-yellow-500" />
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 uppercase tracking-tight">Our Services</h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-sm sm:text-base px-2">
              Specialist formwork company delivering formwork, reinforcement, and concrete services of the highest standard.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <div
                  key={service.id}
                  className="group p-6 sm:p-8 bg-white border border-slate-100 rounded-sm hover:border-yellow-500/50 transition-all duration-300 shadow-sm hover:shadow-lg"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-sm bg-yellow-500/10 flex items-center justify-center mb-4 sm:mb-6 group-hover:bg-yellow-500 transition-colors duration-300">
                    <IconComponent className="w-6 h-6 sm:w-7 sm:h-7 text-yellow-600 group-hover:text-slate-900 transition-colors duration-300" />
                  </div>
                  <h3 className="font-heading text-lg sm:text-xl font-bold text-slate-900 mb-2 sm:mb-3 uppercase">{service.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>

          {/* Service Pillars */}
          <div className="mt-12 sm:mt-16 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {Object.values(serviceDetails).map((detail) => (
              <div key={detail} className="text-center p-3 sm:p-4 bg-slate-50 rounded-sm border-l-4 border-yellow-500">
                <p className="text-slate-700 text-xs sm:text-sm font-medium">{detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Motto Section */}
      <section className="py-16 sm:py-20 bg-slate-900 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-32 h-32 bg-yellow-500/10 rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-48 h-48 bg-yellow-500/10 rounded-full translate-x-1/2 translate-y-1/2" />
        <div className="absolute left-0 top-0 bottom-0 w-1.5 sm:w-2 bg-yellow-500" />
        
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-6">
            <div className="inline-block h-1 w-16 sm:w-24 bg-yellow-500 mb-6 sm:mb-8" />
          </div>
          <blockquote className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white uppercase tracking-tight leading-tight">
            "If you're going to live, leave a legacy. Make a mark on the world that can't be erased."
          </blockquote>
          <div className="mt-6 sm:mt-8">
            <div className="inline-block h-1 w-16 sm:w-24 bg-yellow-500" />
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-16 items-center">
            <div>
              <div className="flex items-center gap-3 sm:gap-4 mb-4">
                <div className="h-1 w-8 sm:w-12 bg-yellow-500" />
                <span className="text-yellow-600 font-bold tracking-wider text-xs sm:text-sm uppercase">Why Barchalo</span>
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 sm:mb-6 uppercase tracking-tight">
                Built on Trust, Delivered with Excellence
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8 sm:mb-10 text-sm sm:text-base">
                For over four decades, we've been the trusted partner for construction projects across South Africa. 
                Our commitment to quality, safety, and client satisfaction sets us apart.
              </p>

              <div className="space-y-5 sm:space-y-6">
                {whyChooseUs.map((item) => (
                  <div key={item.id} className="flex gap-3 sm:gap-4">
                    <div className="w-6 h-6 rounded-sm bg-yellow-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-slate-900" />
                    </div>
                    <div>
                      <h4 className="text-slate-900 font-bold mb-1 text-sm sm:text-base">{item.title}</h4>
                      <p className="text-slate-600 text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative mt-4 lg:mt-0 max-w-md mx-auto lg:max-w-none lg:ml-auto lg:w-[85%]">
              <div className="aspect-square rounded-sm overflow-hidden shadow-xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_87a42c2d-b3ff-4965-9d7d-e392365dc691/artifacts/gank8fdd_WhatsApp%20Image%202026-02-11%20at%205.56.11%20AM%20%282%29.jpeg"
                  alt="Barchalo construction team on site"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              {/* Accent Box */}
              <div className="absolute -bottom-4 -left-4 sm:-bottom-6 sm:-left-6 w-24 h-24 sm:w-32 sm:h-32 bg-yellow-500 rounded-sm flex items-center justify-center shadow-lg">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-slate-900">40+</div>
                  <div className="text-[10px] sm:text-xs font-medium text-slate-800">Years of<br />Excellence</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Portfolio Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 sm:gap-6 mb-10 sm:mb-12">
            <div>
              <div className="flex items-center gap-3 sm:gap-4 mb-4">
                <div className="h-1 w-8 sm:w-12 bg-yellow-500" />
                <span className="text-yellow-600 font-bold tracking-wider text-xs sm:text-sm uppercase">Our Work</span>
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 uppercase tracking-tight">Featured Projects</h2>
            </div>
            <Link
              to="/portfolio"
              className="inline-flex items-center gap-2 text-yellow-600 font-bold uppercase tracking-wide hover:text-yellow-500 transition-colors duration-300 text-sm sm:text-base"
            >
              View All Projects
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {featuredPortfolio.map((item) => (
              <Link
                key={item.id}
                to="/portfolio"
                className="group relative aspect-[4/3] rounded-sm overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300"
                data-testid={`portfolio-item-${item.id}`}
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                <img src={companyInfo.logo} alt="Barchalo" className="absolute bottom-14 right-2 h-7 sm:h-10 w-auto opacity-80 z-10" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-80" />
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                  <span className="inline-block px-2.5 py-1 bg-yellow-500 text-slate-900 text-[10px] sm:text-xs font-bold uppercase rounded-sm mb-2 sm:mb-3">
                    {item.category}
                  </span>
                  <h3 className="text-white font-bold text-base sm:text-lg">{item.title}</h3>
                  {item.client && (
                    <p className="text-slate-300 text-xs sm:text-sm mt-1">{item.client}</p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Strip */}
      <section className="py-12 sm:py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 sm:gap-8 text-center lg:text-left">
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2 uppercase tracking-tight">
                Ready to Start Your Project?
              </h2>
              <p className="text-slate-400 text-sm sm:text-base">
                Get in touch today for a free consultation and quote.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <a
                href={`tel:${companyInfo.phone}`}
                className="inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3 bg-yellow-500 text-slate-900 font-bold uppercase tracking-wide rounded-sm hover:bg-yellow-400 transition-colors duration-300 min-h-[48px] text-sm sm:text-base"
              >
                <Phone className="w-5 h-5" />
                <span className="truncate">{companyInfo.phone}</span>
              </a>
              <a
                href={`mailto:${companyInfo.email}`}
                className="inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3 border-2 border-slate-600 text-white font-bold uppercase tracking-wide rounded-sm hover:border-yellow-500 hover:text-yellow-500 transition-colors duration-300 min-h-[48px] text-sm sm:text-base"
              >
                <Mail className="w-5 h-5" />
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
